import {
  type AppliedRepair,
  type Diagnostic,
  type FormatOptions,
  type MessageCatalog,
  renderDiagnostic,
} from '@jsonfmt/core';

import { groupRepairs, listRepairs, summariseRepairs as summarise } from '../repairSummary';
import type { CommandContext, CommandResult, DocumentSnapshot, Notice } from '../types';

/**
 * BEAUTIFY — pure orchestration over the engine, with exactly three outcomes
 * (docs/ARCHITECTURE.md §5.3, ADR-0013, ADR-0014).
 *
 *   refuse               the document is broken and the eighteen repair rules cannot close
 *                        it. THE DOCUMENT IS NOT TOUCHED, the notice carries the REAL
 *                        diagnostic, and `reveal` points at it.
 *   repair-then-format   the document was broken and is now valid. One WARNING notice that
 *                        names every repair applied, and the success notice is suppressed —
 *                        "at most one toast" is the type, so this is not a policy decision
 *                        made here, it is the only thing expressible.
 *   format               the ordinary path. One success notice.
 *
 * (An empty buffer short-circuits before any of the three, with `toast.error.empty`.)
 *
 * TWO CONTRACT POINTS INHERITED FROM THE LEGACY BUG LIST:
 *
 * 1. ON AUTO-REPAIR THE USER IS TOLD EXACTLY WHAT CHANGED. Not "the JSON was repaired" — the
 *    list, grouped and counted: `trailing comma ×3 · comment ×1`. The labels come from the
 *    engine's own `fix.<rule-id>` catalog through `ctx.catalog`, so they are translated by
 *    whatever locale is loaded and cannot drift from the rule that produced them.
 * 2. A DOCUMENT THAT CANNOT BE PARSED SHOWS THE REAL DIAGNOSTIC. The legacy showed a bare
 *    "Invalid JSON". Here `toast.error.invalid` interpolates `renderDiagnostic(...)`, and
 *    the diagnostic comes from the analysis OF THE UNCHANGED TEXT — which matters, because
 *    `reveal` is an offset into the text still on screen.
 *
 * Everything reached through `ctx.services.analyzer`, never through the core directly: above
 * 256 KiB that call goes to a Worker, and this file does not want to know (ADR-0010).
 */

export type BeautifyMode = FormatOptions['mode'];

/** So a test and the command layer agree on the undo-step label without a literal each. */
export const BEAUTIFY_LABEL: Readonly<Record<BeautifyMode, string>> = {
  pretty: 'beautify.format',
  minify: 'beautify.minify',
};

const SUCCESS_KEY: Readonly<Record<BeautifyMode, string>> = {
  pretty: 'toast.format.success',
  minify: 'toast.compact.success',
};

/** The indent every pretty print in v1 uses. Named so the toast and the printer cannot drift. */
const PRETTY_INDENT_WIDTH = 2;

export async function beautify(
  ctx: CommandContext,
  doc: DocumentSnapshot,
  mode: BeautifyMode,
): Promise<CommandResult> {
  if (doc.text.trim().length === 0) {
    return { notice: emptyNotice(mode) };
  }

  const startedAt = Date.now();
  const direct = await ctx.services.analyzer.format(doc.text, { mode });

  if (direct.ok) {
    return {
      document: { text: direct.text, label: BEAUTIFY_LABEL[mode] },
      notice: successNotice(ctx, mode, doc.text, direct.text, Date.now() - startedAt),
    };
  }

  // The printer refused, and said which diagnostics are blocking. Offer the repair engine
  // the same source — never guess, never print a partially understood document (ADR-0013).
  const repaired = await ctx.services.analyzer.repair(doc.text);

  if (!repaired.after.ok || !repaired.changed) {
    return refuse(ctx, mode, direct.blocking, repaired.after.diagnostics);
  }

  const printed = await ctx.services.analyzer.format(repaired.text, { mode });
  const text = printed.ok ? printed.text : repaired.text;

  return {
    document: { text, label: BEAUTIFY_LABEL[mode] },
    notice:
      // Defensive: a repair that changed the text without recording a single rule has
      // nothing to list, and "Repaired 0 issues:" is worse than saying nothing about it.
      repaired.applied.length === 0
        ? successNotice(ctx, mode, doc.text, text, Date.now() - startedAt)
        : repairNotice(ctx, mode, repaired.applied, ctx.catalog),
  };
}

// ── the three notices ────────────────────────────────────────────────────────────────────

function emptyNotice(mode: BeautifyMode): Notice {
  return {
    tone: 'error',
    key: 'toast.error.empty',
    // A stable id per action: hammering Format on an empty buffer replaces the toast rather
    // than stacking five identical ones (ADR-0014). The legacy stacked freely.
    id: `${BEAUTIFY_LABEL[mode]}.empty`,
  };
}

function successNotice(
  ctx: CommandContext,
  mode: BeautifyMode,
  before: string,
  after: string,
  elapsedMs: number,
): Notice {
  const params =
    mode === 'pretty'
      ? {
          count: countLines(after),
          // `indentAttributive`, not `indent`. The status-bar plural ("2 spaces") and the
          // adjective inside this sentence are not the same string: "Formatted 7 lines with a
          // 2 spaces indent" is ungrammatical, and the two forms diverge further in the
          // languages ADR-0018 expects next. A shared plural that reads correctly in one
          // position and not the other is a false economy.
          indent: ctx.t('editor.status.indentAttributive', { count: PRETTY_INDENT_WIDTH }),
          duration: formatDuration(elapsedMs),
        }
      : // "Collapsed {{count}} lines to a single line" counts what went IN: the result is
        // one line by definition, so counting the output would print "Collapsed 1 line".
        { count: countLines(before), duration: formatDuration(elapsedMs) };

  return { tone: 'success', key: SUCCESS_KEY[mode], params, id: BEAUTIFY_LABEL[mode] };
}

function repairNotice(
  ctx: CommandContext,
  mode: BeautifyMode,
  applied: readonly AppliedRepair[],
  catalog: MessageCatalog,
): Notice {
  return {
    tone: 'warning',
    key: 'toast.repair.appliedDetail',
    params: { count: applied.length, fixes: summariseRepairs(ctx, applied, catalog) },
    id: `${BEAUTIFY_LABEL[mode]}.repaired`,
    // The same repair, as facts rather than as a sentence, for the persistent banner of
    // docs/DESIGN.md §6.5. `formatted: true` is what makes "before formatting" the honest
    // wording HERE — on the paste path nothing is formatted, and that path says so.
    repair: {
      chips: groupRepairs(applied, catalog),
      // The SAME repairs, one row each, for the Repair log (ADR-0101 §7.15). The banner's
      // headline counts the chips and the log's head counts these, and both numbers are
      // `applied.length` because both lists are built from it here rather than recomputed.
      entries: listRepairs(applied, catalog),
      residual: 0,
      formatted: true,
      source: 'format',
    },
  };
}

function refuse(
  ctx: CommandContext,
  mode: BeautifyMode,
  blocking: readonly Diagnostic[],
  afterRepair: readonly Diagnostic[],
): CommandResult {
  // `blocking` describes the text the user is looking at; `afterRepair` describes a string
  // that was never shown and whose offsets would point at the wrong characters. Only the
  // first is safe to `reveal`, and it is also the honest one to quote.
  const first = blocking[0] ?? afterRepair[0];
  const notice: Notice = {
    tone: 'error',
    key: 'toast.error.invalid',
    params: { message: first === undefined ? '' : renderDiagnostic(first, ctx.catalog).message },
    id: `${BEAUTIFY_LABEL[mode]}.invalid`,
  };

  // No `document` key at all: the buffer is left exactly as it was.
  return blocking[0] === undefined ? { notice } : { notice, reveal: blocking[0].span };
}

// ── small pure helpers ───────────────────────────────────────────────────────────────────

/**
 * `trailing comma ×3 · comment ×1`.
 *
 * THE IMPLEMENTATION MOVED to `src/features/repairSummary.ts`, because the editor needs the
 * identical sentence for a document repaired on paste (docs/DESIGN.md §6.5) and may not
 * deep-import this folder (gate 2). This wrapper stays because the signature is the one this
 * feature's own tests speak, and because `ctx.t` is the translator a command already holds.
 */
export function summariseRepairs(
  ctx: CommandContext,
  applied: readonly AppliedRepair[],
  catalog: MessageCatalog,
): string {
  return summarise(ctx.t, applied, catalog);
}

export function countLines(text: string): number {
  if (text.length === 0) return 0;

  let lines = 1;
  for (let i = 0; i < text.length; i += 1) {
    if (text.charCodeAt(i) === 10) lines += 1;
  }
  // A single trailing newline terminates the last line rather than starting a new one.
  return text.charCodeAt(text.length - 1) === 10 ? lines - 1 : lines;
}

/**
 * `8 ms` / `1.4 s`.
 *
 * The unit is not localised and that is a known, small gap: `common.json` has no duration
 * key, and adding one is a locale change the i18n stage owns. Flagged rather than hidden.
 */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '0 ms';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${Math.round(ms / 100) / 10} s`;
}
